// src/Monetization/RecipeImportModal.js
// Modal para importar recetas: pegar texto, subir fichero (.txt/.md/imagen),
// o hacer foto. Las tres opciones están disponibles para todos los usuarios.

import React, { useState, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { API } from '../config/api';
import './RecipeImportModal.css';

const TABS = [
  { id: 'paste', label: '📝 Pegar texto' },
  { id: 'file',  label: '📎 Fichero'     },
  { id: 'photo', label: '📷 Foto'        },
];

const RecipeImportModal = ({ onClose, onImport }) => {
  const [tab, setTab]             = useState('paste');
  const [textInput, setTextInput] = useState('');
  const [file, setFile]           = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  const fileRef  = useRef(null);
  const photoRef = useRef(null);

  const getToken = async () => {
    const user = getAuth().currentUser;
    if (!user) throw new Error('No autenticado');
    return user.getIdToken();
  };

  const readFileAsText = (f) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsText(f, 'utf-8');
    });

  const readFileAsBase64 = (f) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = (e) => resolve(e.target.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });

  const isImageFile = (f) => f && f.type.startsWith('image/');

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setError('');
  };

  const handlePhotoSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
    setError('');
  };

  const parseText = async (text) => {
    const token = await getToken();
    const res = await fetch(API.parseRecipeFromText, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ text }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al parsear');
    return data.recipe;
  };

  const parseImage = async (imageFile) => {
    const token  = await getToken();
    const base64 = await readFileAsBase64(imageFile);
    const res = await fetch(API.parseRecipeFromImage, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ imageBase64: base64, mimeType: imageFile.type }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Error al procesar la imagen');
    return data.recipe;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      let recipe;
      if (tab === 'paste') {
        if (!textInput.trim()) { setError('Pega el texto de la receta'); return; }
        recipe = await parseText(textInput);
      } else if (tab === 'file') {
        if (!file) { setError('Selecciona un fichero'); return; }
        recipe = isImageFile(file)
          ? await parseImage(file)
          : await parseText(await readFileAsText(file));
      } else if (tab === 'photo') {
        if (!photoFile) { setError('Selecciona una foto'); return; }
        recipe = await parseImage(photoFile);
      }
      onImport(recipe);
    } catch (e) {
      setError(e.message || 'No se pudo extraer la receta. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = !loading && (
    (tab === 'paste' && textInput.trim().length > 0) ||
    (tab === 'file'  && !!file) ||
    (tab === 'photo' && !!photoFile)
  );

  return (
    <div className="rim-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rim-modal">

        <div className="rim-header">
          <h2 className="rim-title">📥 Importar receta</h2>
          <button className="rim-close" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        <div className="rim-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`rim-tab${tab === t.id ? ' active' : ''}`}
              onClick={() => { setTab(t.id); setError(''); }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="rim-body">
          {tab === 'paste' && (
            <>
              <p className="rim-hint">
                Pega el texto de cualquier receta — web, libro, chat… La IA la estructura automáticamente.
              </p>
              <textarea
                className="rim-textarea"
                placeholder="Pega aquí el texto de la receta…"
                value={textInput}
                onChange={e => { setTextInput(e.target.value); setError(''); }}
                rows={9}
                autoFocus
              />
            </>
          )}

          {tab === 'file' && (
            <>
              <p className="rim-hint">
                Sube un documento de texto (.txt, .md…) o una imagen del libro / receta escrita.
              </p>
              {file ? (
                <div className="rim-file-selected">
                  <span className="rim-file-name">📄 {file.name}</span>
                  <button className="rim-photo-remove" onClick={() => setFile(null)}>✕ Cambiar</button>
                </div>
              ) : (
                <label className="rim-photo-drop">
                  <span>📎</span>
                  <span>Seleccionar fichero</span>
                  <span className="rim-file-types">.txt, .md, jpg, png…</span>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".txt,.md,.csv,image/*"
                    onChange={handleFileSelect}
                    hidden
                  />
                </label>
              )}
            </>
          )}

          {tab === 'photo' && (
            <>
              <p className="rim-hint">
                Fotografía una página de libro de cocina, carta de restaurante o receta escrita a mano.
              </p>
              {photoPreview ? (
                <div className="rim-photo-preview">
                  <img src={photoPreview} alt="Vista previa" />
                  <button className="rim-photo-remove" onClick={() => { setPhotoFile(null); setPhotoPreview(''); }}>
                    ✕ Cambiar foto
                  </button>
                </div>
              ) : (
                <label className="rim-photo-drop">
                  <span>📷</span>
                  <span>Seleccionar o hacer foto</span>
                  <input
                    ref={photoRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoSelect}
                    hidden
                  />
                </label>
              )}
            </>
          )}

          {error && <p className="rim-error">⚠️ {error}</p>}
        </div>

        <div className="rim-footer">
          <button className="rim-cancel" onClick={onClose} disabled={loading}>Cancelar</button>
          <button className="rim-submit" onClick={handleSubmit} disabled={!canSubmit}>
            {loading ? '⏳ Procesando…' : '✨ Importar receta'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default RecipeImportModal;
